import { useEffect, useRef, useState } from "react";
import type { NodeKind, PoleAddress, PoleParams, TechCalcDictionaries } from "../types";
import {
  constructionGroups,
  fmtDateTime,
  heightForPoleType,
  materialForPoleType,
  poleTypeOptions,
} from "../utils";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-[11px] text-[var(--color-text-secondary)]">{label}</span>
      <div className="mt-0.5">{children}</div>
    </label>
  );
}

/** Adds the current (possibly stale) value as an extra option so the select never
 *  silently swallows a value that dropped out of the dictionary. */
function ValueOption({ value, values }: { value: string; values: string[] }) {
  if (!value || values.includes(value)) return null;
  return <option value={value}>{value} (spoza słownika)</option>;
}

/**
 * Parametry słupa / przyłącza abonenckiego. Selects come from GET /dictionaries/;
 * pole types missing from the norm catalog (resolvable=false) stay visible but
 * disabled with an explicit "brak w katalogu norm" note, so the user knows WHY
 * a type cannot be picked instead of hunting for it.
 */
export function PoleParamsPanel({
  poleRef,
  kind,
  initial,
  address,
  dictionaries,
  onCommit,
  onClose,
}: {
  poleRef: string;
  kind: NodeKind;
  initial: PoleParams;
  /** Read-only OSM address (fetched server-side; never edited here). */
  address?: PoleAddress;
  dictionaries: TechCalcDictionaries | undefined;
  onCommit: (params: PoleParams) => void;
  onClose: () => void;
}) {
  const [params, setParams] = useState<PoleParams>(initial);
  const paramsRef = useRef(params);
  paramsRef.current = params;

  const commit = (patch: Partial<PoleParams>) => {
    const next = { ...paramsRef.current, ...patch };
    setParams(next);
    onCommit(next);
  };

  /** Type change also soft-fills material + height (still editable afterwards). */
  const commitPoleType = (value: string) => {
    const patch: Partial<PoleParams> = { pole_type: value };
    if (value) {
      const material = materialForPoleType(value);
      const height = heightForPoleType(value);
      if (material) patch.material = material;
      if (height) patch.height = height;
    }
    commit(patch);
  };

  // Legacy poles: a type picked before the autofill existed leaves material/height
  // empty — fill ONLY the empty fields once, on panel open.
  useEffect(() => {
    if (!initial.pole_type) return;
    const patch: Partial<PoleParams> = {};
    const material = materialForPoleType(initial.pole_type);
    const height = heightForPoleType(initial.pole_type);
    if (!initial.material && material) patch.material = material;
    if (!initial.height && height) patch.height = height;
    if (Object.keys(patch).length) commit(patch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const materials = dictionaries?.materials ?? [];
  const functions = dictionaries?.functions ?? [];
  const poleTypes = poleTypeOptions(dictionaries);
  const groups = constructionGroups(dictionaries);
  const allConstructions = groups.flatMap((g) => g.values);

  return (
    <div className="p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-secondary)]">
          {kind === "pole" ? "Słup" : "Przyłącze"} {poleRef} — parametry
        </span>
        <button
          type="button"
          className="text-[11px] text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)]"
          onClick={onClose}
        >
          Zamknij
        </button>
      </div>

      <div className="space-y-2">
        <Field label="Typ słupa">
          <select
            className="input-base py-1 text-[12px]"
            value={params.pole_type}
            onChange={(e) => commitPoleType(e.target.value)}
          >
            <option value="">— wybierz —</option>
            <ValueOption value={params.pole_type} values={poleTypes.map((t) => t.value)} />
            {poleTypes.map((t) => (
              <option key={t.value} value={t.value} disabled={!t.resolvable}>
                {t.value}
                {!t.resolvable ? " — brak w katalogu norm" : ""}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Konstrukcja">
          <select
            className="input-base py-1 text-[12px]"
            value={params.construction}
            onChange={(e) => commit({ construction: e.target.value })}
          >
            <option value="">— wybierz —</option>
            <ValueOption value={params.construction} values={allConstructions} />
            {groups.map((g) =>
              g.group ? (
                <optgroup key={g.group} label={g.group}>
                  {g.values.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </optgroup>
              ) : (
                g.values.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))
              ),
            )}
          </select>
        </Field>

        <Field label="Materiał">
          <select
            className="input-base py-1 text-[12px]"
            value={params.material}
            onChange={(e) => commit({ material: e.target.value })}
          >
            <option value="">— wybierz —</option>
            <ValueOption value={params.material} values={materials} />
            {materials.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Wysokość [m]">
          <input
            className="input-base py-1 font-mono text-[12px]"
            placeholder="np. 10.0"
            value={params.height}
            onChange={(e) => commit({ height: e.target.value })}
          />
        </Field>

        <Field label="Funkcja">
          <select
            className="input-base py-1 text-[12px]"
            value={params.function}
            onChange={(e) => commit({ function: e.target.value })}
          >
            <option value="">— wybierz —</option>
            <ValueOption value={params.function} values={functions} />
            {functions.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Nr słupa (pełny)">
          <input
            className="input-base py-1 font-mono text-[12px]"
            value={params.full_number ?? ""}
            onChange={(e) => commit({ full_number: e.target.value })}
          />
        </Field>

        <Field label="Nr historyczny">
          <input
            className="input-base py-1 font-mono text-[12px]"
            value={params.historical_number ?? ""}
            onChange={(e) => commit({ historical_number: e.target.value })}
          />
        </Field>

        {kind === "pole" && (
          <label className="flex cursor-pointer items-start gap-2 border-t border-[var(--color-border)] pt-2">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={!!params.is_lighting}
              onChange={(e) => commit({ is_lighting: e.target.checked })}
            />
            <span>
              <span className="block text-[11px] font-medium text-[var(--color-text-primary)]">
                Oświetleniowy
              </span>
              <span className="block text-[10px] text-[var(--color-text-tertiary)]">
                Do obliczeń dochodzi siła od oprawy Fl = 22 daN (bez zaznaczenia Fl = 0).
              </span>
            </span>
          </label>
        )}

        {kind === "pole" && (
          <label className="flex cursor-pointer items-start gap-2 border-t border-[var(--color-border)] pt-2">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={!!params.out_of_scope}
              onChange={(e) => commit({ out_of_scope: e.target.checked })}
            />
            <span>
              <span className="block text-[11px] font-medium text-[var(--color-text-primary)]">
                Słup spoza opracowania (bez obliczeń)
              </span>
              <span className="block text-[10px] text-[var(--color-text-tertiary)]">
                Przęsła tego słupa nadal obciążają sąsiednie słupy — nie liczymy tylko jego samego.
              </span>
            </span>
          </label>
        )}

        {/* ── adres (read-only, pobierany z OSM po stronie backendu) ──────── */}
        <div className="border-t border-[var(--color-border)] pt-2">
          <p className="section-label mb-1">Adres (OSM)</p>
          {address?.fetched_at ? (
            <div className="font-mono text-[12px] text-[var(--color-text-primary)]">
              <p>{[address.street, address.house_number].filter(Boolean).join(" ") || "— brak ulicy —"}</p>
              <p>{[address.postcode, address.city].filter(Boolean).join(" ") || "— brak miejscowości —"}</p>
              <p className="mt-0.5 text-[10px] text-[var(--color-text-tertiary)]">
                pobrano: {fmtDateTime(address.fetched_at)}
              </p>
            </div>
          ) : (
            <p className="text-[11px] text-[var(--color-text-muted)]">
              Nie pobrano — użyj „Pobierz adresy (OSM)" nad mapą.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
